/**
 * Public Enterprise enquiry form (Phase 11-F).
 */
import {
	Alert,
	Box,
	Button,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { GlassSurface } from "../layouts/components/GlassSurface";
import { usePageHeader } from "../layouts/PageHeaderContext";
import { getApiClient } from "../services/apiClientProvider";

export function EnterpriseContactPage() {
	const [organizationName, setOrganizationName] = useState("");
	const [contactName, setContactName] = useState("");
	const [businessEmail, setBusinessEmail] = useState("");
	const [country, setCountry] = useState("");
	const [expectedTestVolume, setExpectedTestVolume] = useState("");
	const [numberOfUsers, setNumberOfUsers] = useState("");
	const [requiredReporting, setRequiredReporting] = useState("");
	const [integrationNeeds, setIntegrationNeeds] = useState("");
	const [notes, setNotes] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [enquiryId, setEnquiryId] = useState<string | null>(null);

	usePageHeader({
		title: "Enterprise enquiry",
		subtitle: "Contract pricing · Advanced testing · custom limits",
		breadcrumbs: [
			{ label: "Home", to: "/" },
			{ label: "Pricing", to: "/pricing" },
			{ label: "Enterprise" },
		],
	});

	const submit = async () => {
		setError(null);
		setBusy(true);
		try {
			const body: import("@flaha/shared-types").EnterpriseEnquiryRequest = {
				organizationName,
				contactName,
				businessEmail,
			};
			if (country.trim()) body.country = country.trim();
			if (expectedTestVolume.trim()) {
				body.expectedTestVolume = expectedTestVolume.trim();
			}
			if (numberOfUsers.trim()) body.numberOfUsers = numberOfUsers.trim();
			if (requiredReporting.trim()) {
				body.requiredReporting = requiredReporting.trim();
			}
			if (integrationNeeds.trim()) {
				body.integrationNeeds = integrationNeeds.trim();
			}
			if (notes.trim()) body.notes = notes.trim();
			const res = await getApiClient().submitEnterpriseEnquiry(body);
			setEnquiryId(res.enquiryId);
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	};

	if (enquiryId) {
		return (
			<Box sx={{ maxWidth: 560, mx: "auto" }}>
				<GlassSurface intensity="strong" depth="raised" sx={{ p: 3 }}>
					<Alert severity="success" sx={{ mb: 2 }}>
						Thank you. Your Enterprise enquiry was received.
					</Alert>
					<Typography variant="body2" sx={{ mb: 2 }}>
						Reference: <code>{enquiryId}</code>. A Flaha representative will
						contact you at <strong>{businessEmail}</strong> to discuss contract
						limits, Advanced testing, and branding options.
					</Typography>
					<Stack direction="row" spacing={1}>
						<Button component={RouterLink} to="/pricing" variant="outlined">
							Back to pricing
						</Button>
						<Button component={RouterLink} to="/" variant="contained">
							Home
						</Button>
					</Stack>
				</GlassSurface>
			</Box>
		);
	}

	return (
		<Box sx={{ maxWidth: 640, mx: "auto" }}>
			<Typography variant="h4" component="h1" sx={{ mb: 1 }}>
				Contact us for Enterprise
			</Typography>
			<Typography
				variant="body1"
				sx={{ color: "workspace.textSecondary", mb: 3 }}
			>
				Enterprise is contract-based: Advanced levels, custom seats, branding,
				and optional integrations. There is no self-serve checkout.
			</Typography>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
					{error}
				</Alert>
			)}

			<GlassSurface intensity="standard" depth="raised" sx={{ p: 3 }}>
				<Stack spacing={2}>
					<TextField
						required
						label="Organization name"
						value={organizationName}
						onChange={(e) => setOrganizationName(e.target.value)}
						fullWidth
					/>
					<TextField
						required
						label="Contact person"
						value={contactName}
						onChange={(e) => setContactName(e.target.value)}
						fullWidth
					/>
					<TextField
						required
						type="email"
						label="Business email"
						value={businessEmail}
						onChange={(e) => setBusinessEmail(e.target.value)}
						fullWidth
					/>
					<TextField
						label="Country"
						value={country}
						onChange={(e) => setCountry(e.target.value)}
						fullWidth
					/>
					<TextField
						label="Expected test volume"
						value={expectedTestVolume}
						onChange={(e) => setExpectedTestVolume(e.target.value)}
						placeholder="e.g. 200–500 tests / year"
						fullWidth
					/>
					<TextField
						label="Number of users"
						value={numberOfUsers}
						onChange={(e) => setNumberOfUsers(e.target.value)}
						placeholder="e.g. 15"
						fullWidth
					/>
					<TextField
						label="Required reporting"
						value={requiredReporting}
						onChange={(e) => setRequiredReporting(e.target.value)}
						multiline
						minRows={2}
						fullWidth
					/>
					<TextField
						label="Integration requirements"
						value={integrationNeeds}
						onChange={(e) => setIntegrationNeeds(e.target.value)}
						multiline
						minRows={2}
						fullWidth
					/>
					<TextField
						label="Additional notes"
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						multiline
						minRows={2}
						fullWidth
					/>
					<Button
						variant="contained"
						disabled={
							busy ||
							!organizationName.trim() ||
							!contactName.trim() ||
							!businessEmail.trim()
						}
						onClick={() => void submit()}
						sx={{ alignSelf: "flex-start" }}
					>
						{busy ? "Sending…" : "Submit enquiry"}
					</Button>
				</Stack>
			</GlassSurface>
		</Box>
	);
}
